import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ogHomeImg from "@/assets/og/og-home.jpg";
import { hreflangLinks, breadcrumbScript, ogImageMeta, SITE_URL } from "@/lib/seo";
const STORIES_URL = "https://jantrobazar.com/success-stories";
import { createServerFn } from "@tanstack/react-start";
import type { Database } from "@/integrations/supabase/types";
import { ShopVerificationBadge } from "@/components/ShopVerificationBadge";
import { Store, MapPin, Star } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

// Public list of approved shops with real stats — no fabricated numbers.
const listTopShops = createServerFn({ method: "GET" }).handler(async () => {
  const { createClient } = await import("@supabase/supabase-js");
  const supabase = createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    },
  );
  const { data, error } = await supabase
    .from("shops")
    .select(
      "id, slug, name_en, name_bn, district, division, verification_level, rating, rating_count, delivered_order_count, description, created_at",
    )
    .eq("status", "approved")
    .order("delivered_order_count", { ascending: false })
    .limit(12);
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const Route = createFileRoute("/success-stories")({
  loader: async ({ context }) => {
    const shops = await context.queryClient.fetchQuery({
      queryKey: ["success-stories-shops"],
      queryFn: () => listTopShops(),
    });
    return { shops };
  },
  head: ({ loaderData }) => {
    const shops = loaderData?.shops ?? [];
    const scripts: Array<{ type: "application/ld+json"; children: string }> = [
      breadcrumbScript("Success Stories", STORIES_URL),
    ];
    if (shops.length > 0) {
      const itemListLd = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Featured verified sellers on Jantro Bazar",
        itemListOrder: "https://schema.org/ItemListOrderDescending",
        numberOfItems: shops.length,
        itemListElement: shops.map((s: any, i: number) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://jantrobazar.com/shops/${s.slug}`,
          name: s.name_en,
        })),
      };
      scripts.push({ type: "application/ld+json", children: JSON.stringify(itemListLd) });
    }
    return {
      meta: [
        { title: "Seller Success Stories | Jantro Bazar" },
        {
          name: "description",
          content:
            "Real numbers from real Jantro Bazar shops across Bangladesh — no fabricated testimonials, only delivered orders and verified ratings.",
        },
        { property: "og:title", content: "Seller Success Stories | Jantro Bazar" },
        {
          property: "og:description",
          content:
            "Meet verified sellers powering Bangladesh's motorcycle & car parts economy on Jantro Bazar.",
        },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Jantro Bazar" },
        { property: "og:url", content: STORIES_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Seller Success Stories | Jantro Bazar" },
        {
          name: "twitter:description",
          content:
            "Meet verified sellers powering Bangladesh's motorcycle & car parts economy on Jantro Bazar.",
        },
        ...ogImageMeta(`${SITE_URL}${ogHomeImg}`, "Seller Success Stories | Jantro Bazar"),
      ],
      links: [{ rel: "canonical", href: STORIES_URL }, ...hreflangLinks(STORIES_URL)],
      scripts,
    };
  },

  errorComponent: () => <div className="p-16 text-center">Could not load shops.</div>,
  notFoundComponent: () => {
    throw notFound();
  },
  component: SuccessStoriesPage,
});

function SuccessStoriesPage() {
  const { shops } = Route.useLoaderData();
  const { lang } = useT();
  const isBn = lang === "bn";
  return (
    <main className="flex-1 container-app py-10 max-w-5xl">
      <h1 className="text-3xl md:text-4xl font-bold text-primary">
        {isBn ? "বিক্রেতাদের সাফল্যের গল্প" : "Seller Success Stories"}
      </h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">
        {isBn
          ? "নিচের প্রতিটি সংখ্যা সরাসরি মার্কেটপ্লেস থেকে আনা — ডেলিভার হওয়া অর্ডার, যাচাইকৃত গ্রাহক রেটিং এবং বিক্রেতার যাচাই অবস্থা। কোনো ভুয়া টেস্টিমোনিয়াল বা কাল্পনিক পরিসংখ্যান নেই।"
          : "Every number below is pulled live from the marketplace — delivered orders, verified customer ratings, and seller verification status. No fake testimonials, no invented metrics."}
      </p>

      {shops.length === 0 ? (
        <div className="mt-10 text-center py-16 border border-dashed rounded-lg text-muted-foreground">
          {isBn
            ? "এখনো কোনো অনুমোদিত দোকান নেই। শীঘ্রই আবার দেখুন।"
            : "No approved shops yet. Check back soon."}
        </div>
      ) : (
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {shops.map((s: any) => (
            <Link
              key={s.id}
              to="/shops/$slug"
              params={{ slug: s.slug }}
              className="rounded-lg border border-border bg-card p-5 hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Store className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-primary">{s.name_en}</span>
                  </div>
                  {s.name_bn && (
                    <div className="text-xs text-muted-foreground mt-0.5">{s.name_bn}</div>
                  )}
                </div>
                <ShopVerificationBadge level={s.verification_level as any} linkToGuide={false} />
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {s.district}, {s.division}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Star className="h-3 w-3 fill-warning text-warning" />
                  {Number(s.rating ?? 0).toFixed(1)} ({s.rating_count ?? 0}{" "}
                  {isBn ? "রিভিউ" : "reviews"})
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-center">
                <div className="rounded-md bg-secondary py-2">
                  <div className="text-lg font-bold text-primary">
                    {s.delivered_order_count ?? 0}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {isBn ? "ডেলিভার হওয়া অর্ডার" : "delivered orders"}
                  </div>
                </div>
                <div className="rounded-md bg-secondary py-2">
                  <div className="text-lg font-bold text-primary">
                    {new Date(s.created_at).getFullYear()}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {isBn ? "যোগদান" : "joined"}
                  </div>
                </div>
              </div>
              {s.description && (
                <p className="mt-3 text-xs text-muted-foreground line-clamp-2">{s.description}</p>
              )}
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
